import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

const HOVER_OPEN_DELAY_MS = 250;
const HOVER_CLOSE_GRACE_MS = 120;

@customElement("help-jonas-source-pill")
export class HelpJonasSourcePill extends LitElement {
  @property() accessor label = "";
  @property() accessor url = "";
  @property({ attribute: "source-title" }) accessor sourceTitle = "";
  @property() accessor excerpt = "";
  @property() accessor sourcetag = "";
  @property({ attribute: "source-fallback" }) accessor sourceFallback = "Bron";
  @property() accessor kind = "help-article";
  @property({ attribute: "action-label" }) accessor actionLabel = "";
  @property({ type: Boolean, attribute: "opens-in-new-tab" })
  accessor opensInNewTab = false;

  @state() private accessor active = false;
  @state() private accessor hovered = false;

  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  static override styles = css`
    :host {
      display: inline;
    }

    .pill {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin: 0 2px;
      padding: 1px 8px;
      /* Theme-aware AFAS tokens so the pill flips in dark mode (background
         #1b1b1b, text #cad5ed) instead of the old fixed light fallbacks. */
      border: 1px solid var(--color-foreground-300);
      border-radius: 999px;
      background-color: var(--color-background-700);
      color: var(--color-text-on-background-700);
      font: inherit;
      line-height: 1.4;
      cursor: pointer;
      /* Fade the pill in when it mounts (a source resolves inline in a streamed
         answer) so it appears as gently as the surrounding text instead of
         popping in. Runs once on insertion; re-renders keep it steady. */
      animation: jonas-pill-fade-in 240ms ease-out both;
    }

    @keyframes jonas-pill-fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .pill::after {
      content: "";
      position: absolute;
      left: 8px;
      right: 8px;
      bottom: 1px;
      height: 1px;
      background: var(--color-primary-500, #0067b1);
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 250ms ease-out;
    }

    .pill.is-hovered::after,
    .pill:focus-visible::after {
      transform: scaleX(1);
    }

    @media (prefers-reduced-motion: reduce) {
      .pill {
        animation: none;
      }

      .pill::after {
        transition: none;
      }
    }

    .pill__print {
      display: none;
    }

    @media print {
      afas-popover {
        display: none;
      }
      .pill__print {
        display: inline;
        color: var(--color-primary-600, #0067b1);
        text-decoration: underline;
      }
    }

    .card {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: 320px;
      padding: 12px;
      cursor: pointer;
    }

    .card__header {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }

    .card__heading {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .card__title {
      margin: 0;
      font: inherit;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.3;
      color: var(--color-text-on-background);
      /* Show the whole title by wrapping; afas-header would truncate it. */
      overflow-wrap: anywhere;
    }

    .card__source {
      margin: 0;
      font: inherit;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.3;
      color: var(--color-text-on-background-500);
    }

    .card__open-icon {
      flex-shrink: 0;
      --icon-size: 24px;
      color: var(--color-text-on-background-500);
    }

    .card__excerpt {
      margin: 0;
      color: var(--color-text-on-background-800);
      font: inherit;
      font-size: 0.9em;
      line-height: 1.45;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
  `;

  override disconnectedCallback(): void {
    this.clearTimers();
    super.disconnectedCallback();
  }

  protected override render() {
    return html`
      <afas-popover
        ?active=${this.active}
        placement="right"
        arrow
        @afas-popover-close=${this.handlePopoverClose}
      >
        <button
          slot="anchor"
          class="pill ${this.hovered ? "is-hovered" : ""}"
          type="button"
          @pointerenter=${this.handlePointerEnter}
          @pointerleave=${this.handlePointerLeave}
          @focus=${this.handleFocus}
          @blur=${this.handlePointerLeave}
          @keydown=${this.handleKeydown}
          @click=${this.handlePrimaryAction}
        >
          <span class="pill__label">${this.label}</span>
        </button>
        <div
          class="card"
          @click=${this.handlePrimaryAction}
          @pointerenter=${this.cancelClose}
          @pointerleave=${this.handlePointerLeave}
        >
          <div class="card__header">
            <div class="card__heading">
              ${this.sourceTitle
                ? html`<h2 class="card__title">${this.sourceTitle}</h2>`
                : nothing}
              <p class="card__source">
                ${this.sourcetag || this.sourceFallback}
              </p>
            </div>
            ${this.rendersInPanel
              ? nothing
              : html`<afas-icon
                  class="card__open-icon"
                  icon="Launch"
                  size="medium"
                  aria-hidden="true"
                ></afas-icon>`}
          </div>
          ${this.excerpt
            ? html`<p class="card__excerpt">${this.excerpt}</p>`
            : nothing}
        </div>
      </afas-popover>
      <a
        class="pill__print"
        href=${this.url}
        tabindex="-1"
        >${this.label}</a
      >
    `;
  }

  private handlePointerEnter = (): void => {
    this.hovered = true;
    this.clearTimers();
    this.openTimer = setTimeout(() => {
      this.active = true;
    }, HOVER_OPEN_DELAY_MS);
  };

  private handlePointerLeave = (): void => {
    this.clearTimers();
    this.closeTimer = setTimeout(() => {
      this.hovered = false;
      this.active = false;
    }, HOVER_CLOSE_GRACE_MS);
  };

  private handleFocus = (): void => {
    this.hovered = true;
    this.clearTimers();
    this.active = true;
  };

  private cancelClose = (): void => {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.active = false;
      this.hovered = false;
    }
  };

  private handlePopoverClose = (): void => {
    this.active = false;
  };

  private get rendersInPanel(): boolean {
    return this.kind !== "external" && !this.opensInNewTab;
  }

  private handlePrimaryAction = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    this.active = false;
    if (this.rendersInPanel) {
      this.dispatchEvent(
        new CustomEvent("jonas-source-open", {
          detail: { url: this.url, title: this.sourceTitle },
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }
    window.open(this.url, "_blank", "noopener");
  };

  private clearTimers(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.cancelClose();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-source-pill": HelpJonasSourcePill;
  }
}
